import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '~/components/Button';
import { Toggle } from '~/components/Toggle';
import { useUser } from '~/modules/auth/hooks/useUser';
import { useBrand } from '~/modules/branding';
import { DetailPageError } from '~/modules/errors/components/DetailPageError';
import { ErrorContent } from '~/modules/errors/components/ErrorContent';
import { HttpStatusCode } from '~settings/api/lib/types.ts';
import { CTABlock } from '~settings/components/CTABlock';
import { SettingsPageHeader } from '~settings/components/SettingsPageHeader';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import type { LabelFormSchema } from '~settings/modules/label/components/LabelForm';
import { LabelForm } from '~settings/modules/label/components/LabelForm';

const FORM_ID = 'createSublabelFromRequestForm';

export function CreateSublabelFromRequestPage() {
  const brand = useBrand();
  const { hasPermission } = useUser();

  const { t } = useTranslation('settings');
  const [searchParams] = useSearchParams();

  const canCreateLabel = hasPermission('LABEL.CREATE', {
    id: brand.name,
    type: 'APPLICATION',
  });

  const parentId = searchParams.get('parentId') ?? '';
  const notes = searchParams.get('notes') ?? '';
  const name = searchParams.get('name') ?? '';

  const onSubmit = (data: LabelFormSchema) => {
    // awaits for implementation
    // eslint-disable-next-line no-console
    console.log('submit', data);
  };

  if (!canCreateLabel) {
    return <ErrorContent type="noAccess" />;
  }

  const [isActive, setIsActive] = useState<boolean>(true);

  // temporary show error page if parent label is missing
  // as we should handle multiple invalid cases - no label name, label name is not unique, parent label not existing anymore etc
  if (!parentId || !name) {
    return (
      <DetailPageError
        errorCode={HttpStatusCode.NotFound}
        fallbackRoute={{
          href: SETTINGS_ROUTES.LABEL_LIST,
          label: t('backToUserManagement'),
        }}
      />
    );
  }

  return (
    <article className="relative flex min-h-full flex-col">
      <SettingsPageHeader
        breadcrumbItems={[
          { to: SETTINGS_ROUTES.ROOT, label: 'title' },
          { to: SETTINGS_ROUTES.LABEL_LIST, label: 'labels' },
          { to: '', label: 'label.createSublabelTitle' },
        ]}
        title="label.createSublabelTitle"
        actions={
          <Toggle
            pressed={isActive}
            onPressedChange={setIsActive}
            activeLabel={t('active')}
            inactiveLabel={t('inactive')}
          />
        }
      />
      <div className="grow overflow-y-auto pb-xl">
        <LabelForm
          onSubmit={onSubmit}
          isActive={isActive}
          id={FORM_ID}
          notes={notes}
          isSublabelRequest={true}
        />
      </div>

      <CTABlock>
        <Button asChild variant="secondary" size="large">
          <Link to={SETTINGS_ROUTES.LABEL_LIST}>{t('buttons.cancel')}</Link>
        </Button>
        <Button variant="primary" type="submit" form={FORM_ID} size="large">
          {t('buttons.saveChanges')}
        </Button>
      </CTABlock>
    </article>
  );
}

CreateSublabelFromRequestPage.displayName = 'CreateSublabelFromRequestPage';
